import { DataTable, GridColDef, GridRenderCellParams, useGridApiRef } from '@components/DataTable';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import {
  DuplicateCheckable,
  filterNonDuplicates,
  isMediaDuplicate,
} from '@lib/utils/chooseMediaUtils';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { isAudioFile, isImageItem } from '@lib/utils/fileExtensions';
import { formatBytesToString, formatDateToLocale, formatDurationToHHMMSS } from '@lib/utils/units';
import { Box, Skeleton } from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { useDataGridSelection } from '../../../../hooks/useDataGridSelection';
import { useDuplicateChecker } from '../../../../hooks/useDuplicateChecker';
import { DuplicateStatus } from './DuplicateStatus';
import css from './FileUploadDataGrid.module.scss';

// File row data structure with uniqueId for duplicate detection
export interface FileRowData {
  id: string;
  name: string;
  size: number;
  type: string;
  duration?: number;
  width?: number;
  height?: number;
  lastModified: number;
  thumbnail?: string;
  uniqueId?: string; // File fingerprint for duplicate detection
  /** Metadata (thumbnail/duration/dimensions/fingerprint) not yet extracted —
   * the row shows skeletons until this is false. */
  isPending?: boolean;
}

// Memoized cell components
const MemoizedThumbnail = ({ file }: { file: FileRowData }) => {
  const thumbnail = file.thumbnail;
  const width = file.width;
  const height = file.height;

  // Still extracting this file's metadata → skeleton placeholder.
  if (!thumbnail && file.isPending) {
    return (
      <Skeleton
        variant="rectangular"
        className={css.thumbnailImage}
        sx={{ width: '100%', height: '100%', borderRadius: '4px' }}
      />
    );
  }

  if (!thumbnail) {
    const isAudio = isAudioFile(file.name);
    return (
      <div className={css.audioThumbnail}>
        {file.uniqueId && (
          <PlaceholderGradient id={generateIdFromString(file.uniqueId)} blur={20} />
        )}
        {isAudio ? (
          <AudioSelectedIcon className={css.audioIconOverlay} />
        ) : (
          <VideoSelectedIcon className={css.videoIconOverlay} />
        )}
      </div>
    );
  }

  const aspectRatio = width && height ? width / height : 16 / 9;

  return (
    <img
      src={thumbnail}
      alt={`Thumbnail for ${file.name}`}
      className={css.thumbnailImage}
      style={{ aspectRatio: `${aspectRatio}` }}
    />
  );
};
MemoizedThumbnail.displayName = 'MemoizedThumbnail';

const MemoizedNameCell = ({
  file,
  isDuplicate,
}: {
  file: FileRowData;
  isDuplicate: (item: FileRowData) => boolean;
}) => {
  return (
    <div className={css.nameWithThumbnailContainer}>
      <div className={css.thumbnailContainer}>
        <MemoizedThumbnail file={file} />
      </div>
      <div className={css.nameTextWrapper}>
        <span className={css.nameCell}>{file.name}</span>
        <span style={{ marginLeft: '8px' }}>
          <DuplicateStatus
            item={file}
            isDuplicate={isDuplicate as (item: DuplicateCheckable) => boolean}
            showBadge={true}
          />
        </span>
      </div>
    </div>
  );
};
MemoizedNameCell.displayName = 'MemoizedNameCell';

const MemoizedInLibraryCell = ({
  file,
  isDuplicate,
}: {
  file: FileRowData;
  isDuplicate: (item: FileRowData) => boolean;
}) => {
  return (
    <span className={css.duplicateCell}>
      <DuplicateStatus
        item={file}
        isDuplicate={isDuplicate as (item: DuplicateCheckable) => boolean}
        showBadge={false}
        showIcon={true}
      />
    </span>
  );
};
MemoizedInLibraryCell.displayName = 'MemoizedInLibraryCell';

const MemoizedSizeCell = ({ file }: { file: FileRowData }) => {
  return <span className={css.sizeCell}>{formatBytesToString(file.size)}</span>;
};
MemoizedSizeCell.displayName = 'MemoizedSizeCell';

const MemoizedTypeCell = ({ file }: { file: FileRowData }) => {
  return <span className={css.typeCell}>{file.type || '?'}</span>;
};
MemoizedTypeCell.displayName = 'MemoizedTypeCell';

const MemoizedDurationCell = ({ file }: { file: FileRowData }) => {
  // A still image has no length to show — the same em-dash the URL import grid uses for it.
  if (isImageItem(file)) return <span className={css.durationCell}>—</span>;
  const duration = file.duration;
  if (duration === undefined) {
    if (file.isPending) {
      return <Skeleton variant="text" width={48} />;
    }
    return <span className={css.durationCell}>?</span>;
  }
  return <span className={css.durationCell}>{formatDurationToHHMMSS(duration)}</span>;
};
MemoizedDurationCell.displayName = 'MemoizedDurationCell';

const MemoizedDimensionsCell = ({ file }: { file: FileRowData }) => {
  const width = file.width;
  const height = file.height;
  if (!width || !height) {
    if (file.isPending) {
      return <Skeleton variant="text" width={56} />;
    }
    return <span className={css.dimensionsCell}>?</span>;
  }
  return (
    <span className={css.dimensionsCell}>
      {width}×{height}
    </span>
  );
};
MemoizedDimensionsCell.displayName = 'MemoizedDimensionsCell';

const MemoizedLastModifiedCell = ({ file }: { file: FileRowData }) => {
  return <span className={css.lastModifiedCell}>{formatDateToLocale(file.lastModified)}</span>;
};
MemoizedLastModifiedCell.displayName = 'MemoizedLastModifiedCell';

// Ref interface to expose DataGrid selection methods to parent
export interface FileUploadDataGridRef {
  getSelection: () => string[];
  setSelection: (selection: string[]) => void;
  clearSelection: () => void;
}

interface FileUploadDataTableps {
  files: File[];
  onSelectionChange?: (selectedIndexes: string[]) => void;
  selectedIndexes?: string[];
  existingUniqueIds: Record<string, string>; // Map of uniqueId -> mediaId for duplicate detection
  filesWithFingerprints?: Map<File, string>; // Optional map of File -> fingerprint
}

const FileUploadDataGridComponent = forwardRef<FileUploadDataGridRef, FileUploadDataTableps>(
  (
    { files, onSelectionChange, selectedIndexes = [], existingUniqueIds, filesWithFingerprints },
    ref,
  ) => {
    const apiRef = useGridApiRef();

    // Transform files to have id property and uniqueId for DataGrid
    const rows: FileRowData[] = useMemo(
      () =>
        files.map((file, index) => ({
          id: index.toString(),
          name: file.name,
          size: file.size,
          type: file.type,
          duration: (file as any).duration,
          width: (file as any).width,
          height: (file as any).height,
          lastModified: file.lastModified,
          thumbnail: (file as any).thumbnail,
          uniqueId: filesWithFingerprints?.get(file), // Add fingerprint if available
          // `metadataExtracted` flips true when the batch finishes (even if
          // fingerprinting errored), so the row leaves the skeleton state.
          isPending: !(file as { metadataExtracted?: boolean }).metadataExtracted,
        })),
      [files, filesWithFingerprints],
    );

    // Get duplicate checking function - rows conform to DuplicateCheckable interface
    const { isDuplicate } = useDuplicateChecker<FileRowData>({
      data: rows,
      existingUniqueIds,
    });

    // Use the custom hook for DataGrid selection logic with duplicate handling
    const {
      duplicateIds,
      isRowSelectable,
      getRowClassName: getRowClassNameFromHook,
      handleSelectionChange,
      rowSelectionModel,
    } = useDataGridSelection<FileRowData>({
      data: rows,
      existingUniqueIds,
      onSelectionChange: onSelectionChange || (() => {}),
      selectedIds: selectedIndexes,
    });

    // Override getRowClassName to use local CSS module (which hides checkboxes for duplicates)
    const getRowClassName = useCallback(
      (params: any) => {
        return isDuplicate(params.row) ? css.duplicateRowDisabled : '';
      },
      [isDuplicate],
    );

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getSelection: () => {
          try {
            if (apiRef.current?.getSelectedRows) {
              const selectedRows = apiRef.current.getSelectedRows();
              const selectedIds = Array.from(selectedRows.keys()).map(String);
              // Filter out duplicates from reported selection
              return filterNonDuplicates(selectedIds, rows, (item) =>
                isMediaDuplicate(item, existingUniqueIds),
              );
            }
            return [];
          } catch (error) {
            console.error('Failed to get selection from DataGrid:', error);
            return [];
          }
        },
        setSelection: (selection: string[]) => {
          try {
            if (apiRef.current?.setRowSelectionModel) {
              // Include duplicates in visual selection but they won't be selectable
              const visualSelection = [...selection, ...duplicateIds];
              apiRef.current.setRowSelectionModel(visualSelection as any);
            }
          } catch (error) {
            console.error('Failed to set selection in DataGrid:', error);
          }
        },
        clearSelection: () => {
          try {
            if (apiRef.current?.setRowSelectionModel) {
              apiRef.current.setRowSelectionModel([] as any);
            }
          } catch (error) {
            console.error('Failed to clear selection in DataGrid:', error);
          }
        },
      }),
      [existingUniqueIds, rows, duplicateIds],
    );

    // Memoized render functions
    const renderName = useCallback(
      (params: GridRenderCellParams<FileRowData>) => {
        return <MemoizedNameCell file={params.row} isDuplicate={isDuplicate} />;
      },
      [isDuplicate],
    );

    const renderInLibrary = useCallback(
      (params: GridRenderCellParams<FileRowData>) => {
        return <MemoizedInLibraryCell file={params.row} isDuplicate={isDuplicate} />;
      },
      [isDuplicate],
    );

    const renderSize = useCallback((params: GridRenderCellParams<FileRowData>) => {
      return <MemoizedSizeCell file={params.row} />;
    }, []);

    const renderType = useCallback((params: GridRenderCellParams<FileRowData>) => {
      return <MemoizedTypeCell file={params.row} />;
    }, []);

    const renderDuration = useCallback((params: GridRenderCellParams<FileRowData>) => {
      return <MemoizedDurationCell file={params.row} />;
    }, []);

    const renderDimensions = useCallback((params: GridRenderCellParams<FileRowData>) => {
      return <MemoizedDimensionsCell file={params.row} />;
    }, []);

    const renderLastModified = useCallback((params: GridRenderCellParams<FileRowData>) => {
      return <MemoizedLastModifiedCell file={params.row} />;
    }, []);

    // Memoize columns
    const columns: GridColDef<FileRowData>[] = useMemo(
      () => [
        {
          field: 'name',
          headerName: 'File Name',
          flex: 1,
          minWidth: 250,
          renderCell: renderName,
          sortable: true,
          filterable: true,
          type: 'string',
          valueGetter: (value, row: FileRowData) => {
            return row.name;
          },
        },
        {
          field: 'size',
          headerName: 'Size',
          flex: 0.3,
          minWidth: 120,
          renderCell: renderSize,
          sortable: true,
          filterable: true,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row: FileRowData) => {
            return row.size;
          },
        },
        {
          field: 'type',
          headerName: 'Type',
          flex: 0.3,
          minWidth: 150,
          renderCell: renderType,
          sortable: true,
          filterable: true,
          type: 'string',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row: FileRowData) => {
            return row.type || '?';
          },
        },
        {
          field: 'duration',
          headerName: 'Duration',
          flex: 0.25,
          minWidth: 100,
          renderCell: renderDuration,
          sortable: true,
          filterable: false,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row: FileRowData) => {
            return row.duration || 0;
          },
        },
        {
          field: 'dimensions',
          headerName: 'Dimensions',
          flex: 0.25,
          // The header keeps room for its menu button, so 110 truncated "Dimensions".
          minWidth: 140,
          renderCell: renderDimensions,
          sortable: false,
          filterable: false,
          align: 'center',
          headerAlign: 'center',
        },
        {
          field: 'lastModified',
          headerName: 'Last Modified',
          flex: 0.4,
          minWidth: 160,
          renderCell: renderLastModified,
          sortable: true,
          filterable: true,
          type: 'date',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row: FileRowData) => {
            return new Date(row.lastModified);
          },
        },
        {
          field: 'inLibrary',
          headerName: 'In library?',
          flex: 0.3,
          minWidth: 120,
          renderCell: renderInLibrary,
          sortable: true,
          filterable: true,
          type: 'boolean',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row: FileRowData) => {
            return isDuplicate(row);
          },
        },
      ],
      [
        renderName,
        renderSize,
        renderType,
        renderDuration,
        renderDimensions,
        renderLastModified,
        renderInLibrary,
        isDuplicate,
      ],
    );

    return (
      <Box className={css.dataGridContainer}>
        <DataTable<FileRowData>
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          className={css.dataGrid}
          rowHeight={86}
          sortingOrder={['asc', 'desc']}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          getRowSpacing={(params) => ({
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
          })}
          isRowSelectable={isRowSelectable}
          getRowClassName={getRowClassName}
          getRowId={(row) => row.id}
          initialState={{
            columns: {
              columnVisibilityModel: {
                inLibrary: false, // Hide "In library?" column by default
              },
            },
          }}
        />
      </Box>
    );
  },
);
FileUploadDataGridComponent.displayName = 'FileUploadDataGridComponent';

export const FileUploadDataGrid = FileUploadDataGridComponent;
